import type { CheckoutSessionDto } from '@reelplicate/api-client/model';

/** What the provider will charge, tax included, in major units (52.43). */
export type CheckoutTotals = {
  subtotal: number;
  tax: number;
  /** Due now: 0 during a trial. */
  total: number;
  /** Each renewal, tax included; null when the provider doesn't say. */
  recurringTotal: number | null;
  currency: string;
};

export interface CheckoutHandlers {
  /** The provider's form is on screen. */
  onReady: () => void;
  /** Paid (or the card saved, for a trial or a card change). */
  onCompleted: () => void;
  /** The checkout couldn't load or failed for good. Card declines are shown inside it. */
  onError: (message: string) => void;
  /** The amounts changed, e.g. tax once the country is known. */
  onTotals?: (totals: CheckoutTotals) => void;
}

/** Removes the checkout again. */
export type Unmount = () => void;

/**
 * Opens the payment provider's inline checkout inside `container`. The one
 * place that knows which provider's browser SDK to load: the API names it in
 * `session.provider`, so switching vendors never touches the pages.
 */
export async function mountCheckout(
  session: CheckoutSessionDto,
  container: HTMLElement,
  handlers: CheckoutHandlers,
): Promise<Unmount> {
  switch (session.provider) {
    case 'paddle': {
      const { mountPaddleCheckout } = await import('./paddle-checkout');
      return mountPaddleCheckout(session, container, handlers);
    }
    default:
      throw new Error(`Unknown payment provider "${session.provider}"`);
  }
}
